import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner } from "reactstrap";
import DataTable from "react-data-table-component";
import { Add, ViewPackageTitle, PackageTitle } from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl, mediaUrl } from "../../../Utils/FileUpload";
import { Package } from "../../../Types/Tables.type";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn } from "../../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { GeneralSetting } from "../../../Types/Tables.type";

export default function ViewPackage() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Package[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();

  const [baseValues, setBaseValues] = useState({
    base_amount: 0,
    base_customer_credit_point: 0,
    base_call_duration: 0,
    base_agent_point: 0,
    base_agent_payment: 0,
  });
  const [disabledEnable, setDisabledEnable] = useState<string | null>(null);
  const [disableAllEnable, setDisableAllEnable] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchData = async (
    page = 1,
    numPerPage = 10,
    search = "",
    sortColumn = "created_at",
    order = "desc"
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Fetch packages and base values in parallel (base values only once if needed, but keeping simple for now)
      const [packageRes] = await Promise.all([
        axios.get(`${baseUrl}package/getall`, {
          params: {
            page: page,
            per_page: numPerPage,
            search: search.trim(),
            sort_by: sortColumn,
            sort_order: order,
          },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const packages: Package[] = packageRes.data.data;
      const total = packageRes.data.total;

      setFilteredItems(packages);
      setTotalRows(total);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage, filterText, sortBy, sortOrder);
  }, [refresh, filterText, sortBy, sortOrder, perPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, filterText, sortBy, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchData(page, newPerPage, filterText, sortBy, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    setSortBy(column.sortField || "created_at");
    setSortOrder(sortDirection);
    setCurrentPage(1); // Reset to first page on sort
  };

  const handleAdd = () => {
    navigate("/add_package");
  };

  const handleEdit = (id: string) => {
    navigate("/edit_package", { state: { id: id } });
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this package?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button color="danger" onClick={() => confirmDelete(id)}>
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}package/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`Package Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setRefresh((prev) => prev + 1);
      } catch (error) {
        console.error("Error deleting package:", error);
        toast.error("An error occurred while deleting the package", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  const toggleEnable = (id: string, currentStatus: number) => {
    setDisabledEnable(id); // reuse this state if needed
    setDisableAllEnable(true);

    const confirmToggle = () => {
      toast.warn(
        <div>
          <div>
            Are you sure you want to{" "}
            <strong>{currentStatus === 1 ? "disable" : "enable"}</strong> this
            package?
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color={currentStatus === 1 ? "danger" : "success"}
              onClick={() => confirmToggleEnable(id, currentStatus)}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledEnable(null);
                setDisableAllEnable(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisabledEnable(null);
            setDisableAllEnable(false);
          },
        }
      );
    };

    const confirmToggleEnable = async (id: string, currentStatus: number) => {
      setDisabledEnable(null);
      setDisableAllEnable(false);
      toast.dismiss();

      try {
        const newStatus = currentStatus === 1 ? 0 : 1;

        await axios.post(
          `${baseUrl}package/toggle_active`,
          { id, is_active: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success(
          `Package ${newStatus === 1 ? "enabled" : "disabled"} successfully!`,
          {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
            onClose: () => {
              setRefresh((prev) => prev + 1);
            },
          }
        );
      } catch (error) {
        console.error("Error toggling package:", error);
        toast.error("Error updating enable status", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    };

    confirmToggle();
  };
  // Define columns with actions
  const packageTableColumns = [
    {
      name: <div style={{ whiteSpace: "normal" }}>S.No</div>,
      cell: (row: Package, index: number) => (
        <>{(currentPage - 1) * perPage + index + 1}</>
      ),
      width: "6%",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Image</div>,
      cell: (row: Package) => (
        <img
          src={`${mediaUrl}/${row.image}`}
          alt={row.image}
          width={25}
          height={25}
          style={{
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      ),
      // sortable: true,
      width: "7%",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Package Name</div>,
      selector: (row: Package) => row.name,
      sortable: true,
      sortField: "name",
      width: "12%",
    },
    {
      name: (
        <div style={{ whiteSpace: "normal" }}>
          Recharge Amount {currency ? `(${currency})` : ""}
        </div>
      ),
      selector: (row: Package) => row.amount,
      width: "12%",
      sortable: true,
      sortField: "amount",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Customer Points</div>,
      selector: (row: Package) => row.base_customer_credit_point,
      sortable: true,
      sortField: "amount", // Sorted by amount since it's proportional
      width: "10%",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Duration (mins)</div>,
      selector: (row: Package) => row.base_call_duration,
      sortable: true,
      sortField: "amount", // Sorted by amount since it's proportional
      width: "11%",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Agent Points</div>,
      selector: (row: Package) => row.base_agent_point,
      sortable: true,
      sortField: "amount", // Sorted by amount since it's proportional
      width: "10%",
    },
    {
      name: (
        <div style={{ whiteSpace: "normal" }}>
          Agent Payment {currency ? `(${currency})` : ""}
        </div>
      ),
      selector: (row: Package) => row.base_agent_payment,
      sortable: true,
      sortField: "amount", // Sorted by amount since it's proportional
      width: "12%",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Country</div>,
      selector: (row: Package) => row.country?.name,
      sortable: true,
      sortField: "country_id",
      width: "10%",
      wrap: true,
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Actions</div>,
      cell: (row: Package) => (
        <div className="d-flex justify-content-between">
          <Button
            color={row.is_active ? "success" : "secondary"}
            size="sm"
            onClick={() => toggleEnable(row.id, row.is_active)}
            disabled={disabledEnable === row.id || disableAllEnable}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={row.is_active ? "Disable" : "Enable"}
          >
            <i
              className={`fa ${row.is_active ? "fa-check-circle" : "fa-ban"}`}
            />
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={() => handleEdit(row.id)}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Edit"
          >
            <i className="icon-pencil-alt" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
            disabled={disabledDelete === row.id || disableAllDelete}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Delete"
          >
            <i className="icon-trash" />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      // button: true,
      width: "10%",
    },
  ];

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }
  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };
  return (
    <>
      <Breadcrumbs
        pageTitle={ViewPackageTitle}
        parent={PackageTitle}
        title={ViewPackageTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Col xs={12} className="mt-2 d-flex justify-content-end mb-3">
              <Btn color="primary" onClick={handleAdd}>
                {Add}
              </Btn>
            </Col>
            <div className="mb-3">
              <FilterComponent
                onFilter={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilterText(e.target.value)
                }
                filterText={filterText}
              />
            </div>
            <div className="table-responsive">
              <DataTable
                data={filteredItems}
                columns={packageTableColumns}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                sortServer
                onSort={handleSort}
                highlightOnHover
                striped
                customStyles={customStyles}
                progressPending={loading}
                progressComponent={<Spinner color="primary" />}
              />
            </div>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer />
    </>
  );
}
